/**
 * partner_terms_text の [terms] / [privacy] 等を、行の URL に差し込む。
 * 記法は text_1 の [br] と同じ「プレースホルダ」。URL は HTML に直書きしない。
 *
 * - [terms] / [terms:ラベル] → terms_url（既定ラベル: 利用規約）
 * - [privacy] / [privacy:ラベル] → privacy_url（既定ラベル: 個人情報保護方針）
 * - [handling] / [handling:ラベル] → handlingurl（既定: 個人情報の取扱い）
 * - [occupation] / [occupation:ラベル] → occupationurl（既定: 取扱職種の範囲等の明示）
 * - [br] は useRtTextFormat / formatRtBudouxText 側で改行
 */

export type RtPartnerTermsUrls = {
  termurl?: string
  privacyurl?: string
  handlingurl?: string
  occupationurl?: string
}

type RtTermsTokenKind = 'terms' | 'privacy' | 'handling' | 'occupation'

const TOKEN_RE = /\[(terms|privacy|handling|occupation)(?::([^\]]*))?\]/g

const DEFAULT_LABELS: Record<RtTermsTokenKind, string> = {
  terms: '利用規約',
  privacy: '個人情報保護方針',
  handling: '個人情報の取扱い',
  occupation: '取扱職種の範囲等の明示'
}

const URL_KEY: Record<RtTermsTokenKind, keyof RtPartnerTermsUrls> = {
  terms: 'termurl',
  privacy: 'privacyurl',
  handling: 'handlingurl',
  occupation: 'occupationurl'
}

/** BudouX が分割しない私用領域プレースホルダ */
function sentinel(index: number): string {
  return `\uE000${index}\uE001`
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/'/g, '&#39;')
}

export type RtPartnerTermsToken = {
  kind: RtTermsTokenKind
  label: string
}

/** 文字列の partner_terms_text があるときだけ上書き対象（偶然の truthy を除外） */
export function hasPartnerTermsText(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

/**
 * リンクトークンを sentinel に退避する。[br] はそのまま残す。
 */
export function maskRtPartnerTermsTokens(text: string): {
  masked: string
  tokens: RtPartnerTermsToken[]
} {
  TOKEN_RE.lastIndex = 0
  const tokens: RtPartnerTermsToken[] = []
  const masked = text.replace(
    TOKEN_RE,
    (_m, kind: string, customLabel?: string) => {
      const tokenKind = kind as RtTermsTokenKind
      const label =
        typeof customLabel === 'string' && customLabel !== ''
          ? customLabel
          : DEFAULT_LABELS[tokenKind]
      const index = tokens.length
      tokens.push({ kind: tokenKind, label })
      return sentinel(index)
    }
  )
  return { masked, tokens }
}

export function expandRtPartnerTermsToken(
  token: RtPartnerTermsToken,
  urls: RtPartnerTermsUrls
): string {
  const url = String(urls[URL_KEY[token.kind]] || '').trim()
  const label = escapeHtml(token.label)
  if (!url) return label
  return `<a href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer" class="rt-common-terms__link">${label}</a>`
}

/** BudouX / [br] 整形後の文字列にリンクを戻す */
export function unmaskRtPartnerTermsTokens(
  formatted: string,
  tokens: RtPartnerTermsToken[],
  urls: RtPartnerTermsUrls
): string {
  let out = formatted
  tokens.forEach((token, index) => {
    out = out
      .split(sentinel(index))
      .join(expandRtPartnerTermsToken(token, urls))
  })
  return out
}
